<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { OperatorEmployee } from '../types/operator'

const props = defineProps<{
  open: boolean
  employees: OperatorEmployee[]
  selectedId?: number
}>()

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'select', employee: OperatorEmployee): void
}>()

const { t } = useI18n()
const search = ref('')

watch(
  () => props.open,
  (open) => {
    if (open) search.value = ''
  },
)

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return props.employees
  return props.employees.filter((employee) =>
    [employee.employee_code, employee.first_name, employee.last_name, employee.job_title]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term)),
  )
})

function fullName(employee: OperatorEmployee): string {
  return `${employee.first_name} ${employee.last_name}`.trim()
}

function choose(employee: OperatorEmployee): void {
  emit('select', employee)
  emit('close')
}
</script>

<template>
  <div v-if="open" class="modal-overlay" @click.self="emit('close')">
    <div class="modal" role="dialog" aria-modal="true" :aria-label="t('operator.shopfloor.operatorPickerTitle')">
      <header>
        <h3>{{ t('operator.shopfloor.operatorPickerTitle') }}</h3>
        <button type="button" class="close" :aria-label="t('operator.shopfloor.cancel')" @click="emit('close')">×</button>
      </header>
      <input
        v-model="search"
        class="search"
        type="search"
        autofocus
        :placeholder="t('operator.shopfloor.operatorPickerSearch')"
        @keydown.esc="emit('close')"
      />
      <p v-if="!filtered.length" class="empty">{{ t('operator.shopfloor.operatorPickerEmpty') }}</p>
      <ul v-else class="picker-list">
        <li v-for="employee in filtered" :key="employee.id">
          <button
            type="button"
            class="picker-row"
            :class="{ selected: employee.id === selectedId }"
            @click="choose(employee)"
          >
            <span class="code">{{ employee.employee_code }}</span>
            <span class="name">{{ fullName(employee) }}</span>
            <span v-if="employee.job_title" class="job">{{ employee.job_title }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay { position: fixed; z-index: 60; inset: 0; display: grid; padding: 20px; background: rgb(15 30 26 / 45%); place-items: center; }
.modal { display: grid; width: min(520px, 100%); max-height: min(640px, 88vh); grid-template-rows: auto auto 1fr; gap: 10px; padding: 16px; border-radius: 12px; background: #fff; box-shadow: 0 20px 60px rgb(15 30 26 / 30%); }
header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
header h3 { margin: 0; font-size: 16px; color: #1a2b26; }
.close { width: 30px; height: 30px; border: 0; border-radius: 8px; background: #eef2f1; color: #45605a; font-size: 18px; line-height: 1; cursor: pointer; }
.close:hover { background: #e2e9e6; }
.search { padding: 9px 11px; border: 1px solid #cbd6d2; border-radius: 8px; font: inherit; }
.empty { margin: 18px 0; color: #65736e; text-align: center; }
.picker-list { overflow-y: auto; margin: 0; padding: 0; list-style: none; }
.picker-row { display: grid; width: 100%; grid-template-columns: 90px 1fr auto; gap: 10px; align-items: center; padding: 10px 12px; border: 0; border-bottom: 1px solid #eef2f1; background: transparent; color: #22342e; text-align: left; cursor: pointer; }
.picker-row:hover { background: #f2f8f6; }
.picker-row.selected { background: #e5f5f7; box-shadow: inset 3px 0 0 #16869a; }
.picker-row .code { color: #65736e; font-size: 12px; font-weight: 700; }
.picker-row .name { font-size: 13px; font-weight: 700; }
.picker-row .job { color: #65736e; font-size: 12px; }
</style>
